import { useState, useEffect } from 'react'
import type { SortingState, VisibilityState } from '@tanstack/react-table'
import { toast } from 'sonner'
import { Copy } from 'lucide-react'
import { useDebounce } from '@/client/hooks/useDebounce'
import { useCrawlPages, type CrawlPage } from '../hooks/useCrawls'
import { apiClient } from '@/client/lib/api-client'
import { DataTable } from './DataTable'
import { PageDetailSheet } from './PageDetailSheet'
import { pageColumns, DEFAULT_COLUMN_VISIBILITY } from './columns'

export interface FilterOption {
  label: string
  value: string
}

interface FilteredPagesTabProps {
  crawlId: string
  /** Default filter to apply */
  defaultFilter: string
  /** Optional sub-filter dropdown options */
  filterOptions?: FilterOption[]
  /** Default sort column */
  defaultSortBy?: string
  defaultSortDesc?: boolean
  /** Override default column visibility */
  columnVisibility?: VisibilityState
}

export function FilteredPagesTab({
  crawlId,
  defaultFilter,
  filterOptions,
  defaultSortBy = 'url',
  defaultSortDesc = false,
  columnVisibility: initialVisibility,
}: FilteredPagesTabProps) {
  const [search, setSearch] = useState('')
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState(defaultFilter)
  const debouncedSearch = useDebounce(search, 300)

  const [sorting, setSorting] = useState<SortingState>([{ id: defaultSortBy, desc: defaultSortDesc }])
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(50)

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialVisibility || { ...DEFAULT_COLUMN_VISIBILITY },
  )

  // Reset page when filter or search changes
  useEffect(() => {
    setPageIndex(0)
  }, [debouncedSearch, activeFilter])

  const sortCol = sorting[0]
  const { data, isLoading } = useCrawlPages(crawlId, {
    search: debouncedSearch,
    filter: activeFilter,
    limit: pageSize,
    offset: pageIndex * pageSize,
    sortBy: sortCol?.id || defaultSortBy,
    sortDir: sortCol?.desc ? 'desc' : 'asc',
  })

  const totalMatching = data?.total || 0

  async function copyFilteredUrls() {
    try {
      const result = await apiClient.get<{ pages: CrawlPage[] }>(`/api/crawls/${crawlId}/pages`, {
        params: {
          search: debouncedSearch,
          filter: activeFilter,
          limit: 5000,
          offset: 0,
          sort_by: sortCol?.id || defaultSortBy,
          sort_dir: sortCol?.desc ? 'desc' : 'asc',
        },
      })
      const urls = result.pages.map(p => p.url).join('\n')
      if (!urls) {
        toast.info('No URLs to copy for this filter.')
        return
      }
      await navigator.clipboard.writeText(urls)
      toast.success(`Copied ${result.pages.length} URL${result.pages.length === 1 ? '' : 's'} to clipboard.`)
    } catch (err) {
      toast.error(`Copy failed: ${err instanceof Error ? err.message : 'unknown error'}`)
    }
  }

  return (
    <>
      {/* Sub-filter + actions */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        {filterOptions && filterOptions.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {filterOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setActiveFilter(opt.value)}
                className={`rounded-md border px-3 py-1 text-xs transition-colors ${
                  activeFilter === opt.value
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:bg-accent/50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        ) : <div />}
        <button
          onClick={copyFilteredUrls}
          disabled={totalMatching === 0}
          title={totalMatching > 0 ? `Copy all ${totalMatching} matching URLs` : 'No URLs to copy'}
          className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent/50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Copy className="h-3 w-3" />
          Copy URLs ({totalMatching})
        </button>
      </div>

      <DataTable
        columns={pageColumns}
        data={data?.pages || []}
        total={data?.total || 0}
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={(idx, size) => {
          setPageIndex(idx)
          setPageSize(size)
        }}
        sorting={sorting}
        onSortingChange={setSorting}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        isLoading={isLoading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search pages by URL..."
        onRowClick={(row: CrawlPage) => setSelectedPageId(row.id)}
      />
      <PageDetailSheet
        crawlId={crawlId}
        pageId={selectedPageId}
        onClose={() => setSelectedPageId(null)}
      />
    </>
  )
}
